import { TransactionType } from '../api/transaction-types';
import { AccountType } from '../../account/api/account-types';
export var TransactionIcon;
(function (TransactionIcon) {
    TransactionIcon["INFLOW"] = "inflow";
    TransactionIcon["OUTFLOW"] = "outflow";
    TransactionIcon["ADJUSTMENT"] = "adjustment";
    TransactionIcon["ALERT"] = "alert";
    TransactionIcon["TRANSFER"] = "transfer";
    TransactionIcon["UNKNOWN"] = "unknown";
})(TransactionIcon || (TransactionIcon = {}));
/**
 * Determines the appropriate icon for a transaction based on its type and properties
 *
 * @param transaction - Transaction data containing type and amount information
 * @returns The appropriate TransactionIcon enum value
 */
function determineTransactionIcon(transaction) {
    if (transaction.type === TransactionType.TRANSFER) {
        return TransactionIcon.TRANSFER;
    }
    if (transaction.type === TransactionType.ADJUSTMENT) {
        return TransactionIcon.ADJUSTMENT;
    }
    if (transaction.outflow && !transaction.bucketId) {
        return TransactionIcon.ALERT;
    }
    if (transaction.inflow && transaction.inflow > 0) {
        return TransactionIcon.INFLOW;
    }
    if (transaction.inflow && transaction.inflow < 0) {
        return TransactionIcon.ADJUSTMENT;
    }
    if (transaction.outflow && transaction.outflow > 0) {
        return TransactionIcon.OUTFLOW;
    }
    return TransactionIcon.UNKNOWN;
}
/**
 * Determines the label text for a transaction based on its type and properties
 *
 * @param transaction - Transaction data containing type and related names
 * @returns The appropriate label string
 */
function determineTransactionLabel(transaction) {
    if (transaction.type === TransactionType.TRANSFER) {
        return transaction.transferAccountName || '';
    }
    if (transaction.type === TransactionType.ADJUSTMENT) {
        return 'Adjustment';
    }
    if (transaction.inflow && transaction.inflow > 0 && !transaction.bucketId) {
        return 'Inflow';
    }
    if (transaction.inflow && transaction.inflow < 0 && !transaction.bucketId) {
        return 'Adjustment';
    }
    if (transaction.outflow && transaction.outflow > 0 && !transaction.bucketId) {
        return 'Uncategorized';
    }
    return transaction.bucketName;
}
/**
 * Determines the prefix text for a transaction based on its type and the direction of funds.
 *
 * **Explicit Rules:**
 * 1. If neither account is a credit card → "Transfer To" (outflow) or "Transfer From" (inflow).
 * 2. If sending money **to a credit card (outflow from a non-credit card)** → "Payment To".
 * 3. If a **credit card account is receiving money (inflow from a non-credit card)** → "Payment From".
 *
 * @param {TransactionTypeData} transaction - The transaction data.
 * @returns {string} The appropriate prefix string.
 */
function determineTransactionPrefix(transaction) {
    if (transaction.type === TransactionType.TRANSFER) {
        // Case 1: Outflow from a non-credit card to a credit card → "Payment To"
        if (transaction.outflow &&
            transaction.outflow > 0 &&
            transaction.accountType !== AccountType.CREDIT_CARD &&
            transaction.transferAccountType === AccountType.CREDIT_CARD) {
            return 'Payment To';
        }
        // Case 2: Inflow into a credit card from a non-credit card → "Payment From"
        if (transaction.inflow &&
            transaction.inflow > 0 &&
            transaction.accountType === AccountType.CREDIT_CARD &&
            transaction.transferAccountType !== AccountType.CREDIT_CARD) {
            return 'Payment From';
        }
        // Case 3: Standard Transfers (between non-credit card accounts)
        if (transaction.outflow && transaction.outflow > 0) {
            return 'Transfer To';
        }
        if (transaction.inflow && transaction.inflow > 0) {
            return 'Transfer From';
        }
        return 'Transfer';
    }
    return transaction.categoryName;
}
/**
 * Combines transaction display properties into a single object
 *
 * @param transaction - The transaction data object containing type, amounts, and related metadata
 * @returns An object containing icon type, prefix text, and label text
 */
export function getTransactionTypeData(transaction) {
    return {
        icon: determineTransactionIcon(transaction),
        prefix: determineTransactionPrefix(transaction),
        label: determineTransactionLabel(transaction),
    };
}
